import React, { useState, useEffect } from 'react';
// import { Plus } from 'lucide-react';
// import Button from '../components/shared/Button';
import { SidebarItem } from './shared/SidebarItem';
import { mainNavItems, otherNavItems, subAdminNavItems } from '../config/sidebarItems';
import { useNavigate, useLocation } from 'react-router-dom';
import CreateDocumentModal from '../components/modals/createDocumentModal';
import ConfirmationModal from '../components/modals/confirmationModal';
import logoutModalIcon from '../assets/icons/logout-modal-icon.svg'; // make sure this exists
import { useAuthContext } from '../hooks/useAuth';
import EditProfileModal from './modals/editProfileModal';
import ChangePasswordModal from './modals/changePasswordModal';
import { AdminService } from '../services';
import { Toast } from './shared/Toast';
import profileUpdatedModalIcon from '../assets/icons/profile-updated-modal-icon.svg';
import passwordChangeSuccessIcon from '../assets/icons/passwordSetSuccess.svg';
import Modal from './modals/modal';
import { ChevronRight } from 'lucide-react';

export default function Sidebar() {
	const [activeItem, setActiveItem] = useState('dashboard');
	const [isCreateDocumentModalOpen, setIsCreateDocumentModalOpen] = useState(false);
	const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false); // new state for logout modal
	const [isSettingsOpen, setIsSettingsOpen] = useState(false); // New state for Settings dropdown
	const [isUpdateProfileModalOpen, setIsUpdateProfileModalOpen] = useState(false); // New state for Update Profile modal
	const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false); // New state for Change Password modal
	const [passwordChangeSuccessMessage, setPasswordChangeSuccessMessage] = useState(''); // New state for password change success message
	const [isProfileUpdatedModalOpen, setIsProfileUpdatedModalOpen] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();
	const { logout, user, getUserPicture, updateUserData, isLoading } = useAuthContext(); // Destructure isLoading
	const [userProfile, setUserProfile] = useState({
		firstName: user?.firstName || '',
		lastName: user?.lastName || '',
		email: user?.email || '',
		photo: getUserPicture()
	});

	useEffect(() => {
		if (user) {
			setUserProfile({
				firstName: user.firstName || '',
				lastName: user.lastName || '',
				email: user.email || '',
				photo: user.pictureUrl || getUserPicture()
			});
		}
	}, [user, getUserPicture]);

	const handleChangePassword = (message) => {
		setIsChangePasswordModalOpen(false); // Close the change password modal
		setPasswordChangeSuccessMessage(message); // Set the custom success message
		setIsProfileUpdatedModalOpen(true); // Open the profile updated modal, NOT update profile modal
	};

	const closeProfileUpdatedOrPasswordChangedModal = () => {
		setIsProfileUpdatedModalOpen(false);
		if (passwordChangeSuccessMessage) {
			logout();
			navigate('/admin/login', { replace: true });
		}
	};

	const handleSaveProfile = async (updatedProfile) => {
		try {
			// Prepare profile data for API - only include fields that have values
			const profileData = {};

			// Only add fields that have values and are different from current user data
			if (updatedProfile.firstName && updatedProfile.firstName.trim() !== '') {
				profileData.firstName = updatedProfile.firstName.trim();
			}
			if (updatedProfile.lastName && updatedProfile.lastName.trim() !== '') {
				profileData.lastName = updatedProfile.lastName.trim();
			}
			if (updatedProfile.profileImage) {
				profileData.profileImage = updatedProfile.profileImage;
			}

			// Check if at least one field is provided
			if (Object.keys(profileData).length === 0) {
				Toast.error('Please provide at least one field to update');
				return;
			}

			// Call the update profile API
			const response = await AdminService.updateProfile(user.userId, profileData);

			if (response.status === 200 && response.user) {
				// Update auth context with new customer data
				updateUserData(response.user);

				// Clear any previous password change messages
				setPasswordChangeSuccessMessage('');

				// Close modal and show confirmation
				setIsUpdateProfileModalOpen(false); // Close the edit profile modal
				setIsProfileUpdatedModalOpen(true); // Open the profile updated modal
			} else {
				// Handle error
				const errorMessage = response.message || 'Failed to update profile';
				Toast.error(errorMessage);
			}
		} catch (error) {
			Toast.error('Network error. Please try again.');
		}
	};

	// Filter navigation items based on accessibleRoutes
	const filterAccessibleNavItems = (navItems) => {
		const accessibleRoutes = user?.accessibleRoutes || [];
		return navItems.filter((item) => accessibleRoutes.includes(item.path));
	};

	// Combine all possible navigation items and then filter
	const allPossibleNavItems = [...mainNavItems, ...subAdminNavItems];
	const navigationItems = filterAccessibleNavItems(allPossibleNavItems);

	useEffect(() => {
		const currentPath = location.pathname;
		const allNavItemsForEffect = [...navigationItems, ...otherNavItems]; // Use the filtered navigationItems
		const foundItem = allNavItemsForEffect.find((item) => item.path === currentPath);
		if (foundItem) {
			setActiveItem(foundItem.id);
		}
	}, [location.pathname, navigationItems]); // Removed otherNavItems from dependencies

	const handleItemClick = (item) => {
		setActiveItem(item.id);
		navigate(item.path);
	};

	const handleLogout = () => {
		setIsLogoutModalOpen(false); // close modal
		logout();
		navigate('/admin/login', { replace: true }); // navigate after confirmation
	};

	// Show loading state while auth is initializing
	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="text-center">
					<div className="animate-spin rounded-full h-32 w-32 border-b-2 border-dark-blue mx-auto"></div>
					<p className="mt-4 text-dark-blue">Loading...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-white lg:max-w-[273px] max-w-[240px] hidden lg:block">
			<div className="pt-10">
				{/* New Document Button */}
				{/* <div className="px-6 mb-10">
					<Button className="w-full" onClick={() => setIsCreateDocumentModalOpen(true)}>
						<div className="flex items-center justify-center gap-2">
							<Plus className="w-4 h-4 group-hover:text-orange transition-colors duration-200" />
							<span className="group-hover:text-hover-gradient">New Document</span>
						</div>
					</Button>
				</div> */}

				{/* Main Navigation */}
				<div className="px-6 mb-10">
					<h3 className="text-xs font-semibold text-dark-blue opacity-50 uppercase tracking-wider mb-5">
						MENU
					</h3>
					<div className="space-y-2.5">
						{navigationItems.map((item) => (
							<SidebarItem
								key={item.id}
								icon={item.icon}
								label={item.label}
								active={activeItem === item.id}
								variant={item.variant}
								onClick={() => handleItemClick(item)}
							/>
						))}
					</div>
				</div>

				{/* Divider */}
				<div className="mx-6 mb-10">
					<div className="h-px bg-gray-100"></div>
				</div>

				{/* Other Navigation */}
				<div className="px-6">
					<h3 className="text-xs font-semibold text-dark-blue opacity-50 uppercase tracking-wider mb-5">
						Account
					</h3>
					<div className="space-y-2.5">
						{otherNavItems.map((item) => (
							<React.Fragment key={item.id}>
								<SidebarItem
									icon={item.icon}
									label={item.label}
									active={activeItem === item.id}
									variant={item.variant}
									extraContent={
										item.id === 'settings' && (
											<div
												className={`w-4 h-4 transition-transform duration-300 ${
													isSettingsOpen ? 'rotate-90' : 'rotate-0'
												}`}
											>
												<ChevronRight className="w-4 h-4" />
											</div>
										)
									}
									onClick={() => {
										if (item.id === 'logout') {
											setIsLogoutModalOpen(true);
										} else if (item.id === 'settings') {
											setIsSettingsOpen(!isSettingsOpen);
										} else {
											handleItemClick(item);
										}
									}}
								/>
								{item.children && (
									<div
										className={`ml-4 space-y-2.5 overflow-hidden transition-all duration-300 ease-in-out ${
											isSettingsOpen ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0'
										}`}
									>
										{item.children.map((child) => (
											<SidebarItem
												icon={child.icon}
												key={child.id}
												label={child.label}
												active={activeItem === child.id}
												onClick={() => {
													if (child.id === 'profile') {
														setIsUpdateProfileModalOpen(true);
													} else if (child.id === 'security') {
														setIsChangePasswordModalOpen(true);
													} else {
														handleItemClick(child);
													}
												}}
											/>
										))}
									</div>
								)}
							</React.Fragment>
						))}
					</div>
				</div>
			</div>

			{/* Create Document Modal */}
			<CreateDocumentModal
				isOpen={isCreateDocumentModalOpen}
				onClose={() => setIsCreateDocumentModalOpen(false)}
				isImported={false}
			/>

			{/* Logout Confirmation Modal */}
			<ConfirmationModal
				isOpen={isLogoutModalOpen}
				onClose={() => setIsLogoutModalOpen(false)}
				icon={<img src={logoutModalIcon} alt="Logout" className="mx-auto" />}
				heading={<em>Are you sure you want to log out?</em>}
				description={<em>Don't worry — your work is saved, and you can log back in anytime.</em>}
				primaryButtonText="Cancel"
				secondaryButtonText="Logout"
				onPrimaryClick={() => setIsLogoutModalOpen(false)}
				onSecondaryClick={handleLogout}
			/>
			<EditProfileModal
				isOpen={isUpdateProfileModalOpen}
				onClose={() => setIsUpdateProfileModalOpen(false)}
				userProfile={userProfile}
				onSaveProfile={handleSaveProfile}
			/>
			<ChangePasswordModal
				isOpen={isChangePasswordModalOpen}
				onClose={() => setIsChangePasswordModalOpen(false)}
				onChangePassword={handleChangePassword}
			/>

			<Modal
				isOpen={isProfileUpdatedModalOpen}
				onClose={() => {
					setIsProfileUpdatedModalOpen(false);
					setPasswordChangeSuccessMessage(''); // Clear message on close
				}}
				icon={
					<img
						src={passwordChangeSuccessMessage ? passwordChangeSuccessIcon : profileUpdatedModalIcon}
						alt={passwordChangeSuccessMessage ? 'Password Changed' : 'Profile Setting Updated'}
						className="mx-auto"
					/>
				}
				heading={
					passwordChangeSuccessMessage ? 'Password changed successfully' : 'Profile setting updated'
				}
				description={passwordChangeSuccessMessage || 'Your account profile has been updated.'}
				buttonText="Close"
				onButtonClick={() => closeProfileUpdatedOrPasswordChangedModal()}
			/>
		</div>
	);
}
